import { ChangeDetectionStrategy, Component, computed, input, model, signal, TemplateRef } from '@angular/core';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  TMenuAction,
  TTableColumn,
  TTableRenderComponent,
  TTableStyles,
  ACTIONS_CELL_PADDING_REM,
  CONTROL_COLUMN_WIDTH,
  FLEX_COLUMN_MIN_WIDTH,
  ICON_ACTION_WIDTH_REM,
  MENU_COLUMN_WIDTH,
} from '@dss-libs/shared/type/common-types';
import { ConditionFunctionPipe, RenderFunctionPipe } from '@dss-libs/shared/ui/common-pipes';
import { AutoHideScrollbarDirective } from '@dss-libs/shared/ui/common-directives';
import { BaseTableComponent } from '../../../abstract/base-table/base-table.component';
import { MenuComponent } from '../menu/menu.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'dss-libs-ui-table',
  imports: [
    NgComponentOutlet,
    NgTemplateOutlet,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatCheckboxModule,
    MatIconButton,
    MatIcon,
    MatTooltip,
    TranslocoDirective,
    AutoHideScrollbarDirective,
    MenuComponent,
    ButtonComponent,
    RenderFunctionPipe,
    ConditionFunctionPipe,
  ],
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T, A> extends BaseTableComponent<T, A> {
  readonly sortDir = model<SortDirection | undefined>();
  readonly sortColumn = model<string | undefined>();
  readonly tableStyles = input<Partial<TTableStyles>>({});
  readonly buttonActionsWidth = input<string>();

  protected readonly activeRow = signal<T | undefined>(undefined);
  protected readonly expandedRow = signal<T | undefined>(undefined);

  protected readonly styles = computed<TTableStyles>(() => ({
    roundedTop: true,
    roundedBottom: true,
    filled: false,
    ...this.tableStyles(),
  }));

  protected readonly buttonActionsColumnWidth = computed<string>(
    () =>
      this.buttonActionsWidth() ??
      `${this.buttonActions().length * ICON_ACTION_WIDTH_REM + ACTIONS_CELL_PADDING_REM}rem`
  );

  protected readonly buttonActionsFlex = computed<string | null>(() =>
    this.buttonActions().length ? `0 0 ${this.buttonActionsColumnWidth()}` : null
  );

  protected readonly tableMinWidth = computed<string>(() => {
    const tracks = this.columns().map((column) => column.minWidth ?? FLEX_COLUMN_MIN_WIDTH);
    if (this.selectedRows()) tracks.push(CONTROL_COLUMN_WIDTH);
    if (this.expandedDetails()) tracks.push(CONTROL_COLUMN_WIDTH);
    if (this.buttonActions().length) tracks.push(this.buttonActionsColumnWidth());
    if (this.menuActions().length) tracks.push(MENU_COLUMN_WIDTH);
    return `calc(${tracks.join(' + ')})`;
  });

  protected readonly expandedDetailsTemplate = computed<TemplateRef<unknown> | undefined>(() => {
    const expandedDetails = this.expandedDetails();
    return expandedDetails instanceof TemplateRef ? expandedDetails : undefined;
  });

  protected readonly expandedDetailsComponent = computed<TTableRenderComponent<T, unknown> | undefined>(() => {
    const expandedDetails = this.expandedDetails();
    return expandedDetails && !(expandedDetails instanceof TemplateRef) ? expandedDetails : undefined;
  });

  protected readonly displayedColumns = computed<string[]>(() => {
    const columns: string[] = [];
    if (this.selectedRows()) columns.push('select');
    if (this.expandedDetails()) columns.push('expand');
    columns.push(...this.columns().map((column) => column.id));
    if (this.buttonActions().length) columns.push('button-actions');
    if (this.menuActions().length) columns.push('menu-actions');
    return columns;
  });

  protected readonly rowMenuActions = computed<TMenuAction<A>[]>(() => {
    const row = this.activeRow();
    if (!row) return [];
    return this.menuActions()
      .filter((action) => !action.hiddenFn?.(row))
      .map(({ action, label, iconMetaData, disabled, disabledFn }) => ({
        action,
        label,
        svgIcon: iconMetaData?.icon,
        disabled: disabled || (disabledFn?.(row) ?? false),
      }));
  });

  protected readonly displayFooter = computed<boolean>(() => this.columns().some((column) => column.footerFn));

  readonly #selectedKeys = computed<Set<unknown>>(() => {
    const keyFn = this.rowKeyFn();
    return new Set((this.selectedRows() ?? []).map(keyFn));
  });

  protected readonly isCurrentPageSelected = computed<boolean>(() => {
    const keys = this.#selectedKeys();
    const keyFn = this.rowKeyFn();
    const data = this.data();
    return data.length > 0 && data.every((row) => keys.has(keyFn(row)));
  });

  protected readonly isCurrentPagePartiallySelected = computed<boolean>(() => {
    const keys = this.#selectedKeys();
    const keyFn = this.rowKeyFn();
    return !this.isCurrentPageSelected() && this.data().some((row) => keys.has(keyFn(row)));
  });

  protected isRowSelected(row: T): boolean {
    return this.#selectedKeys().has(this.rowKeyFn()(row));
  }

  protected cellValue(row: T, column: TTableColumn<T>): string {
    const value = column.value === undefined ? undefined : row[column.value];
    return value === undefined || value === null || value === '' ? '---' : String(value);
  }

  protected toggleCurrentPageRows(event: MatCheckboxChange) {
    if (event.checked) this.#selectRows(this.data());
    else this.#deselectRows(this.data());
  }

  protected toggleRowSelection(event: MatCheckboxChange, row: T) {
    if (event.checked) this.#selectRows([row]);
    else this.#deselectRows([row]);
  }

  #selectRows(rows: T[]) {
    const keyFn = this.rowKeyFn();
    this.selectedRows.update((current) => {
      const selected = current ?? [];
      const keys = new Set(selected.map(keyFn));
      return [...selected, ...rows.filter((row) => !keys.has(keyFn(row)))];
    });
  }

  #deselectRows(rows: T[]) {
    const keyFn = this.rowKeyFn();
    const removedKeys = new Set(rows.map(keyFn));
    this.selectedRows.update((current) => (current ?? []).filter((row) => !removedKeys.has(keyFn(row))));
  }

  protected toggleRowExpansion(row: T) {
    this.expandedRow.update((expanded) => (expanded === row ? undefined : row));
  }

  protected rowActivated(row: T) {
    if (!this.isRowClickable()) return;
    if (this.expandedDetails()) this.toggleRowExpansion(row);
    this.rowClickOutput.emit(row);
  }

  protected sortChanged(sortEvent: Sort) {
    this.sortColumn.set(sortEvent.active);
    this.sortDir.set(sortEvent.direction);
  }

  protected actionClicked(action: A, row: T | undefined = this.activeRow()) {
    if (!row) return;
    this.actionOutput.emit({ action, row });
  }
}
